import List from '@monofront/design-system/visuals/Icons/Old/List';
import AddButtonWithLabel from '@monofront/design-system/components/AddButtonWithLabel';
import Label from '@monofront/design-system/components/Form/Label';
import Checkbox from '@monofront/design-system/components/Form/Checkbox';
import { redirectToLogin } from '@utils/redirect';
import { useAuthentication } from '@context/businessContainer';
import { useWishlist } from '@context/businessContainer';
import Popover from '@components/Popover';
import { useRouter } from 'next/router';
import {
  IProduct,
  IWishlist,
  ISearchProduct,
  IShoppingCartProduct,
} from '@monofront/types';
import CreateWishlistModal from '@components/CreateWishlistModal';
import { fireAutoMLEvent, dataLayerTrackEvent } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import { zindex } from '@monofront/theme';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import { getProductCategory } from '@monofront/business/utils';
import { ProductAddToWishlist } from '@monofront/design-system/components/ProductCard';

interface IButtonAddToWishlistProps {
  product: ISearchProduct | IShoppingCartProduct;
}

const ButtonAddToWishlist: React.FC<IButtonAddToWishlistProps> = ({
  product,
}) => {
  const { isAuthenticated } = useAuthentication();
  const {
    wishlists,
    addProductToWishlist,
    deleteProductFromWishlist,
    isProductInOneWishlist,
  } = useWishlist();
  const router = useRouter();
  const { currency } = useI18n();

  const handleAddToWishlist = async (wishlistUid: string) => {
    if (!isAuthenticated) {
      redirectToLogin(router.asPath);

      return;
    }

    await addProductToWishlist(wishlistUid, product.objectID);

    // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
    fireAutoMLEvent('add-to-list', {
      productEventDetail: {
        productDetails: [
          {
            id: `${product.sku}`,
            currencyCode: 'EUR',
            originalPrice: product.compare_at_price,
            displayPrice: product.price,
          },
        ],
        listId: wishlistUid,
      },
    });

    dataLayerTrackEvent('productAddedToWishlist', {
      productName: product.title,
    });

    trackEvent({
      type: 'LFAddToWishlist',
      data: {
        products: [
          {
            name: product.title,
            sku: product.sku,
            price: product.price * 100,
            brand: product.vendor,
            category: getProductCategory(product) ?? undefined,
            quantity: 1,
          },
        ],
        currency,
      },
    });
  };

  const handleDeleteFromWishlist = async (wishlistUid: string) => {
    if (!isAuthenticated) {
      redirectToLogin(router.asPath);

      return;
    }

    await deleteProductFromWishlist(wishlistUid, product.objectID);

    fireAutoMLEvent('remove-from-list', {
      productEventDetail: {
        productDetails: [
          {
            id: product.sku,
            currencyCode: 'EUR',
            originalPrice: product.compare_at_price,
            displayPrice: product.price,
          },
        ],
        listId: wishlistUid,
      },
    });
  };

  const tWishlists = useTranslations('Wishlists');

  return (
    <>
      <div
        className="wishlist tw-absolute !tw-my-0 tw-h-6 tw-w-6 tw-justify-self-end tw-grid-area-[wishlist]"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Popover
          trigger="click"
          isLight
          interactive
          zIndex={zindex.modalBackdrop - 1} // to allow the NewList modal to pass over
          appendTo={global?.document?.body}
          placement="bottom-start"
          content={
            <div className="list">
              <div className="title-modal">
                <List />
                <p>{tWishlists('addToWishlist')}</p>
              </div>
              <ul>
                {wishlists.map((wishlist: IWishlist) => {
                  const isProductInWishlist = wishlist.products.some(
                    ({ objectID }: IProduct) => objectID === product.objectID,
                  );

                  return (
                    <li key={wishlist.id}>
                      <div
                        onClick={() => handleDeleteFromWishlist(wishlist.id)}
                        className="list-item"
                      >
                        <Label id={wishlist.id}>
                          {wishlist.defaultWishlist
                            ? `${tWishlists('defaultWishlistName')} 💛`
                            : wishlist.name}
                        </Label>
                        <Checkbox
                          name="wishlist"
                          id={wishlist.id}
                          required={false}
                          checked={isProductInWishlist}
                          onChange={() => {
                            return !isProductInWishlist
                              ? handleAddToWishlist(wishlist.id)
                              : handleDeleteFromWishlist(wishlist.id);
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="separator" />
              <CreateWishlistModal
                triggerButtonRender={(setIsCreateWishlistModalOpen) => (
                  <div
                    className="add-list"
                    onClick={() => {
                      if (!isAuthenticated) {
                        redirectToLogin(router.asPath);

                        return;
                      }

                      setIsCreateWishlistModalOpen(true);
                    }}
                  >
                    <AddButtonWithLabel handleClick={() => undefined}>
                      {tWishlists('createList')}
                    </AddButtonWithLabel>
                  </div>
                )}
              />
            </div>
          }
        >
          <ProductAddToWishlist
            isProductInOneWishlist={isProductInOneWishlist(product.objectID)}
          />
        </Popover>
      </div>
      <style jsx>
        {`
          .title-modal {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
          }

          .title-modal > p {
            margin: var(--space-2) 0 var(--space-3) 0;
            font-weight: var(--font-weight-base);
          }

          .separator {
            border: var(--border);
            margin: var(--space-2) 0;
          }

          .list {
            display: flex;
            flex-direction: column;
            padding: var(--space-5);
            min-width: 270px;
            font-size: var(--font-size-2);
          }

          .list > ul > li {
            margin-bottom: var(--space-2);
            text-align: left;
          }

          .list > ul > li,
          .add-list {
            cursor: pointer;
          }

          .add-list {
            display: flex;
            align-items: center;
          }

          .add-list > span {
            margin-left: var(--space-3);
          }

          .list-item {
            display: flex;
            justify-content: space-between;
          }
        `}
      </style>
    </>
  );
};

export default ButtonAddToWishlist;
